<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header-left">
          <div class="panel-icon"><i class="fa fa-list-alt"></i></div>
          <div>
            <div class="panel-title">Service ORI</div>
            <div class="panel-sub">Manage ORI numbers and their associated service configurations</div>
          </div>
        </div>
        <div class="panel-header-right">
          <button class="btn-add" @click="showModal">
            <i class="fa fa-plus"></i> Add ORI
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="filter-bar">
        <div class="filter-left">
          <div class="search-box">
            <i class="fa fa-search search-icon"></i>
            <input v-model="searchInput" type="text" class="search-input" placeholder="Search ORI or profession…" />
            <button v-if="searchInput" class="search-clear" @click="searchInput = ''">
              <i class="fa fa-times"></i>
            </button>
          </div>
          <select class="filter-select" v-model="select_service">
            <option value="">All Services</option>
            <option v-for="(item, index) in getServiceCodeData.data" :key="index" :value="item.id">
              {{ item.service_name }}
            </option>
          </select>
          <button class="btn-search" @click="search()">
            <i class="fa fa-search"></i> Search
          </button>
        </div>
      </div>

      <!-- Table -->
      <div class="table-wrap">

        <!-- Loading -->
        <div class="state-row" v-if="loadingData">
          <i class="fa fa-spinner fa-spin"></i> Loading…
        </div>

        <!-- Empty -->
        <div class="state-row" v-else-if="paginatedItems.length === 0">
          <i class="fa fa-inbox" style="font-size:28px; color:#dde2ec; margin-bottom:8px;"></i>
          <div>No ORI records found</div>
        </div>

        <!-- Data -->
        <table v-else>
          <colgroup>
            <col style="width:52px" />
            <col style="width:140px" />
            <col style="min-width:160px" />
            <col style="min-width:150px" />
            <col style="width:130px" />
            <col style="width:130px" />
            <col style="width:100px" />
            <col style="width:60px" />
          </colgroup>
          <thead>
            <tr>
              <th>#</th>
              <th>ORI</th>
              <th>Profession</th>
              <th>Service Code</th>
              <th class="col-center">Photo Req.</th>
              <th class="col-center">Txn Type</th>
              <th class="col-center">Rate</th>
              <th class="col-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in paginatedItems" :key="item.id">
              <td class="td-num">{{ (currentPage - 1) * pageSize + index + 1 }}</td>
              <td class="td-mono">{{ item.ori_number }}</td>
              <td>{{ item.profession }}</td>
              <td>{{ item.service_code_name }}</td>
              <td class="col-center">
                <span :class="['badge-photo', item.requires_photograph ? 'badge-yes' : 'badge-no']">
                  <i :class="item.requires_photograph ? 'fa fa-check' : 'fa fa-times'"></i>
                  {{ item.requires_photograph ? 'Yes' : 'No' }}
                </span>
              </td>
              <td class="col-center">
                <span class="badge-txn" v-if="item.transaction_type">{{ item.transaction_type }}</span>
                <span class="td-empty" v-else>—</span>
              </td>
              <td class="col-center">{{ item.our_rate ? '$' + item.our_rate : '—' }}</td>
              <td class="col-center td-action" style="overflow:visible; padding:8px 6px;">
                <button class="action-btn" @click.stop="toggleMenu($event, item.id)">
                  <i class="fa fa-ellipsis-v"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-bar" v-if="!loadingData && filteredItems.length > 0">
        <div class="pagination-info">
          Showing {{ (currentPage - 1) * pageSize + 1 }}–{{ Math.min(currentPage * pageSize, filteredItems.length) }}
          of {{ filteredItems.length }}
          <select class="page-size-select" v-model="pageSize" @change="currentPage = 1">
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
          </select>
          per page
        </div>
        <div class="pagination-pages">
          <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">
            <i class="fa fa-chevron-left"></i>
          </button>
          <button v-for="p in visiblePages" :key="p" class="page-btn" :class="{ active: p === currentPage }"
            @click="currentPage = p">{{ p }}</button>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="currentPage++">
            <i class="fa fa-chevron-right"></i>
          </button>
        </div>
      </div>

    </div>

    <!-- 3-dot dropdown -->
    <div v-if="openMenuId !== null" class="dropdown-menu-overlay" @click="openMenuId = null"></div>
    <div v-if="openMenuId !== null" class="dropdown-menu-pop" :style="menuStyle">
      <button class="drop-item" @click="editFromMenu()"><i class="fa fa-pencil-alt"></i> Edit</button>
      <button class="drop-item drop-item-danger" @click="deleteFromMenu()"><i class="fa fa-trash"></i> Delete</button>
    </div>

    <!-- Delete confirm modal -->
    <transition name="del-fade">
      <div class="del-overlay" v-if="showDeleteConfirm" @click.self="showDeleteConfirm = false">
        <div class="del-card">
          <div class="del-icon-wrap">
            <i class="fa fa-trash"></i>
          </div>
          <div class="del-title">Delete ORI</div>
          <div class="del-body">
            Are you sure you want to delete
            <strong>{{ deleteTarget ? deleteTarget.ori_number : '' }}</strong>?
            <br />This action cannot be undone.
          </div>
          <div class="del-footer">
            <button class="del-btn del-cancel" @click="showDeleteConfirm = false">Cancel</button>
            <button class="del-btn del-confirm" @click="confirmDelete" :disabled="deleting">
              <i v-if="deleting" class="fa fa-spinner fa-spin"></i>
              <i v-else class="fa fa-trash"></i>
              {{ deleting ? 'Deleting…' : 'Delete' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal -->
    <serviceori v-if="ModalShow" v-on:closemodal="closeModal" :show-modal="ModalShow" :serviceori="serviceOriData"
      :is_edit="is_edit" />
  </div>
</template>

<script>
import {
  FETCH_SERVICEORI,
  DELETE_SERVICEORI,
  FETCH_SERVICECODES
} from "@/core/services/store/actions.type";
import serviceori from "@/pages/backend/servicesori/serviceori";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";

export default {
  components: { serviceori },
  computed: {
    ...mapGetters(["currentUser", "getServiceOriData", "getServiceCodeData"]),
    filteredItems() {
      let list = this.getServiceOriData.data || [];
      if (this.searchInput) {
        const q = this.searchInput.toUpperCase();
        list = list.filter(item =>
          (item.ori_number || "").toUpperCase().includes(q) ||
          (item.profession || "").toUpperCase().includes(q)
        );
      }
      return list;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.pageSize));
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredItems.slice(start, start + this.pageSize);
    },
    visiblePages() {
      const pages = [];
      for (let p = Math.max(1, this.currentPage - 2); p <= Math.min(this.totalPages, this.currentPage + 2); p++) {
        pages.push(p);
      }
      return pages;
    }
  },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.$store.dispatch(FETCH_SERVICECODES);
    this.getServiceOri();
  },
  data() {
    return {
      loadingData: false,
      ModalShow: false,
      select_service: "",
      searchInput: "",
      is_edit: false,
      currentPage: 1,
      pageSize: 10,
      openMenuId: null,
      menuStyle: {},
      activeMenuItem: null,
      showDeleteConfirm: false,
      deleteTarget: null,
      deleting: false,
      serviceOriData: {
        id: 0, ori_number: "", profession: "",
        our_rate: "", service_code: "", transaction_type: ""
      },
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "ORI List", route: "" }
      ]
    };
  },
  methods: {
    search() {
      this.searchInput = "";
      this.currentPage = 1;
      this.getServiceOri();
    },
    showModal() {
      this.serviceOriData = { id: 0, ori_number: "", profession: "", our_rate: "", service_code: "", transaction_type: "" };
      this.ModalShow = true;
      this.is_edit = false;
    },
    closeModal() {
      this.ModalShow = false;
      this.$store.dispatch(FETCH_SERVICEORI, { service_code_id: this.select_service });
    },
    edit(item) {
      this.ModalShow = true;
      this.is_edit = true;
      this.serviceOriData = { ...item };
    },
    getServiceOri() {
      this.loadingData = true;
      this.currentPage = 1;
      this.$store.dispatch(FETCH_SERVICEORI, { service_code_id: this.select_service })
        .then(() => { this.loadingData = false; })
        .catch(() => { this.loadingData = false; });
    },
    toggleMenu(event, id) {
      if (this.openMenuId === id) { this.openMenuId = null; return; }
      const rect = event.currentTarget.getBoundingClientRect();
      this.menuStyle = {
        position: "fixed",
        top: (rect.bottom + 4) + "px",
        left: (rect.right - 140) + "px",
        zIndex: 9999
      };
      this.openMenuId = id;
      this.activeMenuItem = this.filteredItems.find(i => i.id === id);
    },
    editFromMenu() {
      this.edit(this.activeMenuItem);
      this.openMenuId = null;
    },
    deleteFromMenu() {
      this.deleteTarget = this.activeMenuItem;
      this.openMenuId = null;
      this.showDeleteConfirm = true;
    },
    confirmDelete() {
      this.deleting = true;
      this.$store.dispatch(DELETE_SERVICEORI, this.deleteTarget.id)
        .then(() => {
          this.deleting = false;
          this.showDeleteConfirm = false;
          this.$toastr.s("Deleted successfully");
          this.getServiceOri();
        })
        .catch(err => {
          this.deleting = false;
          this.$toastr.e(err);
        });
    }
  }
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$green: #276749;
$orange: #F58020;
$dark: #0d1b3e;
$border: #dde2ec;

.page-wrap {
  padding: 24px;
  font-family: $font;
}

/* Panel */
.panel-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(13, 27, 62, 0.08);
  overflow: clip;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 18px;
  border-bottom: 1px solid #f0f3fa;
  background: #fafbff;
}

.panel-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.panel-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, $blue, #2176ae);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  flex-shrink: 0;
}

.panel-title {
  font-size: 15px;
  font-weight: 700;
  color: $dark;
}

.panel-sub {
  font-size: 12px;
  color: #8a94a6;
  margin-top: 2px;
}

.btn-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  border: none;
  background: $blue;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: background 0.15s;

  i {
    font-size: 11px;
  }

  &:hover {
    background: #2d85bb;
  }
}

/* Filters */
.filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #f0f3fa;
  background: #fafbff;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
  border: 1.5px solid $border;
  border-radius: 8px;
  background: #fff;
  width: 220px;

  &:focus-within {
    border-color: $blue;
  }
}

.search-icon {
  position: absolute;
  left: 10px;
  color: #a0aab8;
  font-size: 12px;
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 34px;
  border: none;
  border-radius: 8px;
  padding: 0 30px 0 30px;
  font-size: 13px;
  font-family: $font;
  outline: none;
  background: transparent;
  color: $dark;
}

.search-clear {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  color: #a0aab8;
  cursor: pointer;
  font-size: 11px;
  padding: 0;

  &:hover {
    color: $dark;
  }
}

.filter-select {
  height: 34px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 28px 0 10px;
  font-size: 13px;
  font-family: $font;
  color: $dark;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23a0aab8'/%3E%3C/svg%3E") no-repeat right 10px center;
  appearance: none;
  outline: none;

  &:focus {
    border-color: $blue;
  }
}

.btn-search {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 7px;
  border: 1.5px solid $blue;
  background: transparent;
  color: $blue;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;

  i {
    font-size: 11px;
  }

  &:hover {
    background: #eef7fb;
  }
}

/* Table */
.table-wrap {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 460px;
}

.state-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  color: #8a94a6;
  font-size: 13px;
  gap: 6px;

  i {
    font-size: 18px;
    color: #c8d0dc;
  }
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-family: $font;
  table-layout: fixed;
}

thead {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f7f9fc;
  border-bottom: 2px solid $border;
}

th {
  padding: 10px 14px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;

  &.col-center {
    text-align: center;
  }
}

td {
  padding: 11px 14px;
  border-bottom: 1px solid #f0f3fa;
  color: $dark;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.col-center {
    text-align: center;
  }
}

tbody tr:last-child td {
  border-bottom: none;
}

tbody tr:hover td {
  background: #f7fbff;
}

.td-num {
  color: #9aa0ae;
  font-size: 12px;
}

.td-mono {
  font-family: 'Courier New', monospace;
  font-size: 12.5px;
  font-weight: 600;
  color: $dark;
}

.td-empty {
  color: #c8d0dc;
}

/* Badges */
.badge-photo {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;

  i {
    font-size: 9px;
  }

  &.badge-yes {
    background: #e6f9f0;
    color: #276749;
  }

  &.badge-no {
    background: #fef2f2;
    color: #b91c1c;
  }
}

.badge-txn {
  display: inline-block;
  padding: 2px 9px;
  border-radius: 20px;
  background: #eef7fb;
  color: $blue;
  font-size: 11px;
  font-weight: 600;
}

/* 3-dot action */
.td-action {
  text-align: center;
}

.action-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1.5px solid $border;
  background: #fff;
  color: #6b7280;
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    border-color: $blue;
    color: $blue;
  }
}

/* Pagination */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid #f0f3fa;
  flex-wrap: wrap;
  gap: 10px;
}

.pagination-info {
  font-size: 12px;
  color: #8a94a6;
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-size-select {
  height: 26px;
  border: 1.5px solid $border;
  border-radius: 6px;
  padding: 0 4px;
  font-size: 12px;
  font-family: $font;
  color: $dark;
  background: #fff;
  outline: none;
  cursor: pointer;
}

.pagination-pages {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-btn {
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1.5px solid $border;
  background: #fff;
  color: #4a5568;
  font-size: 12px;
  font-family: $font;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled):not(.active) {
    border-color: $blue;
    color: $blue;
  }

  &.active {
    background: $blue;
    border-color: $blue;
    color: #fff;
    font-weight: 700;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
}

/* Delete confirm modal */
.del-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(13, 27, 62, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.del-card {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 8px 40px rgba(13, 27, 62, 0.18);
  padding: 36px 32px 28px;
  width: 360px;
  max-width: 90vw;
  text-align: center;
}

.del-icon-wrap {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #fff0f0;
  border: 2px solid #ffd6d6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;

  i {
    font-size: 24px;
    color: #c62828;
  }
}

.del-title {
  font-size: 17px;
  font-weight: 700;
  color: $dark;
  margin-bottom: 10px;
}

.del-body {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 28px;

  strong {
    color: $dark;
  }
}

.del-footer {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.del-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 40px;
  padding: 0 24px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  i {
    font-size: 12px;
  }
}

.del-cancel {
  border: 1.5px solid $border;
  background: #fff;
  color: #4a5568;
  flex: 1;

  &:hover {
    border-color: #9aa0ae;
    background: #f7f9fc;
  }
}

.del-confirm {
  border: none;
  background: #c62828;
  color: #fff;
  flex: 1;

  &:hover {
    background: #a61e1e;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.del-fade-enter-active,
.del-fade-leave-active {
  transition: opacity 0.18s;
}

.del-fade-enter,
.del-fade-leave-to {
  opacity: 0;
}

/* Dropdown pop */
.dropdown-menu-overlay {
  position: fixed;
  inset: 0;
  z-index: 9998;
}

.dropdown-menu-pop {
  background: #fff;
  border: 1px solid $border;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(13, 27, 62, 0.13);
  padding: 4px;
  min-width: 140px;
}

.drop-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-family: $font;
  color: $dark;
  cursor: pointer;
  border-radius: 7px;
  text-align: left;

  i {
    font-size: 12px;
    color: #8a94a6;
    width: 14px;
  }

  &:hover {
    background: #f5f7fb;
  }

  &.drop-item-danger {
    color: #c62828;

    i {
      color: #c62828;
    }

    &:hover {
      background: #fff5f5;
    }
  }
}
</style>
